import React, { useEffect, useState } from 'react';
import {
	List,
	Datagrid,
	TextField,
	ReferenceInput,
	AutocompleteInput,
	FunctionField,
	SelectInput,
	DateInput,
	useListContext,
	TextInput,
} from 'react-admin';
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
	CONNECTOR_PROCESS_STOP_WITH_ERROR,
	connectorAccountProcessStatus,
	IConnectorAccountProcessStats,
	loadStats,
	resolutionStatusLst,
} from '../../Services/connectorAccountProcess';
import { differenceInSeconds, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ResolutionModal } from './Modals/ResolutionModal';

export const StatsBanner = () => {
	const { filterValues } = useListContext();
	const [stats, setStats] = useState<IConnectorAccountProcessStats | null>(
		null
	);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const controller = new AbortController();
		setLoading(true);

		// Construit l'objet JSON attendu
		const filterObj: Record<string, string> = {};
		Object.entries(filterValues).forEach(([key, val]) => {
			if (val !== undefined && val !== null && val !== '') {
				filterObj[key] = String(val);
			}
		});

		// Sérialise l'objet comme react-admin
		const query = new URLSearchParams({
			filter: JSON.stringify(filterObj),
		});

		loadStats(query.toString())
			.then((data) => {
				setStats(data);
				setLoading(false);
			})
			.catch((err) => {
				if (err.name !== 'AbortError') {
					console.error('Error fetching stats:', err);
				}
				setLoading(false);
			});

		return () => controller.abort();
	}, [filterValues]);

	if (loading) return <CircularProgress size={20} />;

	if (!stats) return null;

	return (
		<Card sx={{ mb: 2 }}>
			<CardContent>
				<Typography variant="subtitle1">Statistiques :</Typography>
				<Box display="flex" gap={3} mt={1}>
					<Typography variant="body2">Total : {stats.totalCount}</Typography>
					<Typography variant="body2">
						In progress : {stats.countInProgress}
					</Typography>
					<Typography variant="body2">
						Success : {stats.countSuccess}
					</Typography>
					<Typography variant="body2">
						Error : {stats.countWithError}
					</Typography>
					<Typography variant="body2">
						Inserted rows : {stats.countInsertedRows.toLocaleString('en-US')}
					</Typography>
					<Typography variant="body2">
						Active rows : {stats.countActiveRows.toLocaleString('en-US')}
					</Typography>
				</Box>
			</CardContent>
		</Card>
	);
};

const LogFilters = [
	<ReferenceInput
		key="sku"
		source="sku"
		reference="connectors"
		alwaysOn
		filter={{
			parentSkuId: ['reverse', 'advertiser', 'sales', 'analytics', 'tag', 'custom'],
		}}
	>
		<AutocompleteInput optionText="name" optionValue="sku" />
	</ReferenceInput>,
	<ReferenceInput
		key="projectId"
		source="projectId"
		reference="projects"
		filter={{}}
		alwaysOn
	>
		<AutocompleteInput optionText="name" />
	</ReferenceInput>,
	<ReferenceInput
		key="billingAccountId"
		source="billingAccountId"
		reference="billing-accounts"
		alwaysOn
	>
		<AutocompleteInput optionText="name" />
	</ReferenceInput>,
	<SelectInput
		key="status"
		source="status"
		choices={connectorAccountProcessStatus.map((status) => ({
			id: status.id,
			name: status.name,
		}))}
		alwaysOn
	/>,
	<SelectInput
		key="processType"
		source="processType"
		label="Proceess Type"
		choices={[
			{ id: 'force', name: 'Force' },
			{ id: 'history', name: 'History' },
			{ id: 'cron', name: 'Cron' },
		]}
		alwaysOn
	/>,
	<TextInput key="accountId" source="accountId" label="Account ID" />,
	<TextInput
		key="projectFolderId"
		source="projectFolderId"
		label="Project Folder ID"
	/>,
	<SelectInput
		key="dateFilterType"
		source="dateFilterType"
		label="Date"
		choices={[
			{ id: 'today', name: "Aujourd'hui" },
			{ id: 'yesterday', name: 'Hier' },
			{ id: 'beforeYesterday', name: 'Avant-hier' },
			{ id: 'custom', name: 'Personnalisé' },
		]}
		alwaysOn
	/>,
	<DateInput key="dateFrom" source="dateFrom" label="Du" alwaysOn />,
	<DateInput key="dateTo" source="dateTo" label="Au" alwaysOn />,
	<SelectInput
		key="testMode"
		source="testMode"
		label="Mode Test"
		choices={[
			{ id: 'true', name: 'Test E2E' },
			{ id: 'false', name: 'Production' },
		]}
		alwaysOn
	/>,
];

export const LogList = (props: any) => {
	const getStatusName = (status: number, resolutionStatus: number) => {
		let statusObject = connectorAccountProcessStatus.find(
			(item) => item.id === status
		);

		if (status == CONNECTOR_PROCESS_STOP_WITH_ERROR) {
			statusObject = resolutionStatusLst.find(
				(item) => item.id === resolutionStatus
			);
		}

		return statusObject ? statusObject.name : 'Unknown status';
	};

	const getStatusColor = (status: number, resolutionStatus: number): string => {
		let statusObject = connectorAccountProcessStatus.find(
			(item) => item.id === status
		);

		if (status == CONNECTOR_PROCESS_STOP_WITH_ERROR) {
			statusObject = resolutionStatusLst.find(
				(item) => item.id === resolutionStatus
			);
		}

		return statusObject?.color ?? 'black';
	};

	const formatDuration = (seconds: number): string => {
		const h = Math.floor(seconds / 3600)
			.toString()
			.padStart(2, '0');
		const m = Math.floor((seconds % 3600) / 60)
			.toString()
			.padStart(2, '0');
		const s = Math.floor(seconds % 60)
			.toString()
			.padStart(2, '0');
		return `${h}:${m}:${s}`;
	};

	const [openResolutionModal, setOpenResolutionModal] = useState(false);
	const [selectedProcess, setSelectedProcess] = useState<any | undefined>(
		undefined
	);

	return (
		<>
			<List {...props} filters={LogFilters} perPage={50} sort={{ field: 'startedAt', order: 'DESC' }}>
				<StatsBanner />
				<Datagrid
					rowClick="show"
					sx={{
						'& td.column-sku': { minWidth: 100 },
						'& td.column-projectName': { minWidth: 270 },
						'& td.column-processType': { minWidth: 150 },
						'& td.column-version': { minWidth: 100 },
						'& td.column-status': { minWidth: 200 },
						'& td.column-lookback': { minWidth: 100 },
						'& td.column-started': { minWidth: 250 },
						'& td.column-duration': { minWidth: 250 },
						'& td.column-errorMessage': { minWidth: 300 },
					}}
				>
					<TextField source="projectName" cellClassName="column-projectName" />
					<TextField source="sku" cellClassName="column-sku" />
					<TextField source="processType" cellClassName="column-processType" />
					<FunctionField
						label="Lookback"
						cellClassName="column-lookback"
						render={(record) => {
							const start = new Date(record.processStartDate);
							const end = new Date(record.processEndDate);
							const diffTime = Math.abs(end.getTime() - start.getTime());
							const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

							return `${diffDays} jours`;
						}}
					/>
					<TextField
						source="Acc.processVersion"
						label="Version"
						cellClassName="column-version"
					/>
					<FunctionField
						label="Status"
						cellClassName="column-status"
						render={(record) => {
							const now = new Date();
							const updatedAt = record.updatedAt
								? new Date(record.updatedAt)
								: undefined;

							const tooOld =
								updatedAt &&
								(now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60) > 24 &&
								(record.status === 0 || record.status === 50);

							return (
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<Typography
										style={{
											display: 'inline-block',
											color: getStatusColor(
												record.status,
												record.resolutionStatus
											),
											fontWeight: 'bold',
										}}
									>
										{getStatusName(record.status, record.resolutionStatus)}
									</Typography>
									{tooOld && (
										<WarningAmberOutlinedIcon
											sx={{ color: '#ff9800', ml: 1 }}
											titleAccess="Problème : non actualisé depuis plus de 30 minutes"
											fontSize="small"
										/>
									)}
								</Box>
							);
						}}
					/>
					<FunctionField
						label="Démarré à"
						cellClassName="column-started"
						render={(record) => {
							try {
								const startedAt = new Date(record.startedAt);

								const startedAtStr = format(
									startedAt,
									"dd MMM yyyy 'à' HH:mm",
									{
										locale: fr,
									}
								);

								return startedAtStr;
							} catch (e) {
								return '—';
							}
						}}
					/>
					<FunctionField
						label="Durée"
						cellClassName="column-duration"
						render={(record) => {
							try {
								const startedAt = new Date(record.startedAt);
								const endedAt = record.endedAt
									? new Date(record.endedAt)
									: new Date();
								const seconds = differenceInSeconds(endedAt, startedAt);

								return formatDuration(seconds);
							} catch (e) {
								return '—';
							}
						}}
					/>
					<FunctionField
						label="Détail erreur"
						cellClassName="column-errorMessage"
						render={(record) =>
							record.resolutionMessage && record.resolutionMessage.trim() !== ''
								? record.resolutionMessage +
								  `${
										record.issueJira != '' &&
										' (Issue Jira: ' + record.issueJira + ')'
								  }`
								: record.errorDetails
						}
					/>
					<FunctionField
						label=""
						render={(record) => (
							<ActionMenuButton
								record={record}
								onResolve={(rec) => {
									setSelectedProcess(rec);
									setOpenResolutionModal(true);
								}}
							/>
						)}
					/>
				</Datagrid>
			</List>
			<ResolutionModal
				open={openResolutionModal}
				handleClose={() => setOpenResolutionModal(false)}
				handleSuccess={() => {
					setOpenResolutionModal(false);
					setSelectedProcess(undefined);
				}}
				process={selectedProcess}
			/>
		</>
	);
};

const ActionMenuButton = ({
	record,
	onResolve,
}: {
	record: any;
	onResolve: (record: any) => void;
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleResolveClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		onResolve(record);
		handleClose();
	};

	return (
		<>
			<IconButton onClick={handleClick}>
				<MoreVertIcon />
			</IconButton>
			<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem onClick={handleResolveClick}>Résoudre</MenuItem>
				{/* Ajoute d'autres actions si nécessaire */}
			</Menu>
		</>
	);
};
